// Sections and destinations of the Settings list panel (Settings-General mockup).
// The admin panel lives here too: "Admin panel" in the avatar menu opens Call queues
// (Shell-UserMenu mockup); the other admin pages sit in the Administration section.

export type SettingsItemId =
    | 'general' | 'profile' | 'phones' | 'groups' | 'queues' | 'audio' | 'permissions' | 'help'
    | 'reports' | 'teamAdmin' | 'groupsAdmin' | 'phonesAdmin' | 'integrations' | 'call-restrictions';

export interface SettingsItem {
    id: SettingsItemId;
    label: string;
    description: string;
    /** In-app route; `href` opens outside the app. */
    to?: string;
    href?: string;
    /** Shown to admins only (the page sits behind AdminRoute). */
    adminOnly?: boolean;
}

export interface SettingsSection {
    title: string;
    items: SettingsItem[];
}

export const KNOWLEDGE_BASE_URL = 'https://support.phonehq.com/en';

export const SETTINGS_SECTIONS: SettingsSection[] = [
    {
        title: 'Account',
        items: [
            {id: 'general', label: 'General', description: 'Account, calls, notifications', to: '/settings'},
            {id: 'profile', label: 'Profile', description: 'Name, status, availability', to: '/profile'},
        ],
    },
    {
        title: 'Calls',
        items: [
            {id: 'phones', label: 'Phone numbers', description: 'Lines, hours, messages, queue', to: '/phoneNumbers'},
            {id: 'groups', label: 'Groups', description: 'Teams you call and are called with', to: '/groups'},
            {id: 'queues', label: 'Call queues', description: 'Routing, menus, strategies', to: '/callQueues', adminOnly: true},
            {id: 'audio', label: 'Audio', description: 'Microphone and speaker', to: '/settings/audio'},
        ],
    },
    {
        title: 'Administration',
        items: [
            {id: 'reports', label: 'Reports', description: 'Usage and billing', to: '/admin/report', adminOnly: true},
            {id: 'teamAdmin', label: 'Team members', description: 'Invite people, roles, numbers', to: '/admin/team', adminOnly: true},
            {id: 'groupsAdmin', label: 'All groups', description: 'Every group in the company', to: '/admin/groups', adminOnly: true},
            {id: 'phonesAdmin', label: 'All phone numbers', description: 'Every line in the company', to: '/admin/phoneNumbers', adminOnly: true},
            {id: 'integrations', label: 'Integrations', description: 'Apps, webhooks, API keys', to: '/admin/integrations', adminOnly: true},
            {id: 'call-restrictions', label: 'Call restrictions', description: 'Numbers people can call and forward to', to: '/admin/call-restrictions', adminOnly: true},
        ],
    },
    {
        title: 'Privacy & security',
        items: [
            {id: 'permissions', label: 'Permissions', description: 'Microphone and notifications in this browser', to: '/settings/permissions'},
        ],
    },
    {
        title: 'About',
        items: [
            {id: 'help', label: 'Help & support', description: 'Opens the knowledge base in a new tab', href: KNOWLEDGE_BASE_URL},
        ],
    },
];

/** Which Settings destination a path belongs to, e.g. /phoneNumbers/12 -> phones. */
export function activeSettingsItem(pathname: string): SettingsItemId | undefined {
    if (pathname === '/settings' || pathname === '/settings/') return 'general';
    if (pathname.startsWith('/settings/audio')) return 'audio';
    if (pathname.startsWith('/settings/permissions')) return 'permissions';
    if (pathname.startsWith('/profile')) return 'profile';
    if (pathname.startsWith('/phoneNumbers')) return 'phones';
    if (pathname.startsWith('/groups')) return 'groups';
    if (pathname.startsWith('/callQueues')) return 'queues';
    if (pathname.startsWith('/admin/report')) return 'reports';
    if (pathname.startsWith('/admin/team')) return 'teamAdmin';
    if (pathname.startsWith('/admin/groups')) return 'groupsAdmin';
    if (pathname.startsWith('/admin/phoneNumbers')) return 'phonesAdmin';
    if (/^\/admin\/(integrations|webhook|api)(\/|$)/.test(pathname)) return 'integrations';
    if (pathname.startsWith('/admin/call-restrictions')) return 'call-restrictions';
    return undefined;
}

/** Admin pages shown in the Settings frame (Emergency Notifications has its own rail item). */
export function isAdminSettingsPath(pathname: string): boolean {
    return pathname.startsWith('/admin') && isSettingsPath(pathname);
}

/** The items a role sees: admins get the admin-only ones; platform support gets only those. */
export function visibleSettingsSections(role: string | undefined): SettingsSection[] {
    const isAdmin = role === 'ADMIN' || role === 'SUPERADMIN';
    return SETTINGS_SECTIONS
        .map((section) => ({
            ...section,
            items: section.items.filter((item) => role === 'SUPERADMIN' ? item.adminOnly : isAdmin || !item.adminOnly),
        }))
        .filter((section) => section.items.length > 0);
}

export function isSettingsPath(pathname: string): boolean {
    return activeSettingsItem(pathname) !== undefined;
}
